// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartDefaultConfig.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import { chain } from 'lodash/fp';
/* CHART TYPE */
const CHART_TYPE = {
  // izenda -value             //title - text
  Line: 'Line',
  Column: 'Column',
  Bar: 'Bar',
  Area: 'Area',
  Pie: 'Pie',
  Funnel: 'Funnel',
  Donut: 'Donut',
  Combination: 'Combination',
  TreeMap: 'TreeMap',
  HeatMap: 'HeatMap',
  Bubble: 'Bubble',
  Scatter: 'Scatter',
  Waterfall: 'Waterfall',
  Sparkline: 'Sparkline',

  SolidGauge: 'SolidGauge',
  LinearGauge: 'LinearGauge',
  SimpleGauge: 'SimpleGauge',

  SimpleKPI: 'SimpleKPI',

  Map: 'Map'
};

/* STACK TYPE CHART */
const STACK_TYPE_CHART = {
  get izNormal() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_NORMAL');
  },
  get izPercent() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_PERCENTAGE');
  }
};

const STACK_TYPE_CHART_VALUES = {
  izNormal: 'normal',
  izPercent: 'percent'
};

/* LINE STYLE: border, line-style */
const LINE_STYLE = {
  get solid() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SOLID');
  },
  get dotted() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_DOT');
  },
  get dashed() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_DASH');
  }
};

const LINE_STYLE_VALUES = {
  solid: 'Solid',
  dotted: 'Dot',
  dashed: 'Dash'
};

/* TEXT ORIENTATION: data label, x/y label */
const LABEL_ROTATION = {
  izNormal: 'Normal',
  izCounterclockwise: 'Counterclockwise',
  izClockwise: 'Clockwise',
  izTextup: 'Textup',
  izTextdown: 'Textdown'
};

const LABEL_ROTATION_VALUES = {
  izNormal: 0,
  izCounterclockwise: -45,
  izClockwise: 45,
  izTextup: -90,
  izTextdown: 90
};

/* AXIS ALIGN */
const AXIS_ALIGN = {
  izHigh: 'high',
  izMiddle: 'middle',
  izLow: 'low'
};

const AXIS_ALIGN_VALUES = {
  izHigh: 'high',
  izMiddle: 'middle',
  izLow: 'low'
};

/* HORIZONTAL ALIGN */
const HORIZONTAL_ALIGN = {
  get izCenter() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_CENTER');
  },
  get izLeft() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_LEFT');
  },
  get izRight() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_RIGHT');
  }
};

const HORIZONTAL_ALIGN_VALUES = {
  izCenter: 'center',
  izLeft: 'left',
  izRight: 'right'
};

/* VERTICAL ALIGN */
const VERTICAL_ALIGN = {
  get izTop() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_TOP');
  },
  get izMiddle() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_MIDDLE');
  },
  get izBottom() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_BOTTOM');
  }
};

const VERTICAL_ALIGN_VALUES = {
  izTop: 'top',
  izMiddle: 'middle',
  izBottom: 'bottom'
};

const LAYOUT_ALIGN = {
  get izHorizontal() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LAYOUT_HORIZONTAL');
  },
  get izVertical() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LAYOUT_VERTICAL');
  }
};

const LAYOUT_ALIGN_VALUES = {
  izHorizontal: 'horizontal',
  izVertical: 'vertical'
};

/* ZOOM TYPE */
const ZOOM_TYPE = {
  izX: 'X',
  izY: 'Y',
  izXY: 'XY'
};

const ZOOM_TYPE_VALUES = {
  izX: 'x',
  izY: 'y',
  izXY: 'xy'
};

/* STEP TYPE */
const STEP_TYPE = {
  get izLeft() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_LEFT');
  },
  get izCenter() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_CENTER');
  },
  get izRight() {
    return localizer.getText('CHART_DEFAULT_CONFIG_LABEL_RIGHT');
  }
};

const STEP_TYPE_VALUES = {
  izLeft: 'left',
  izCenter: 'center',
  izRight: 'right'
};

/* RANGE */
const RANGE = {
  get rangeOnly() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_RANGE_ONLY');
  },
  get rangeWithAverage() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_RANGE_WITH_AVERAGE');
  }
};

const PIE_CHART_STYPE = {
  get basic() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_BASIC');
  },
  get ring() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_RING');
  },
  get semiCirle() {
    return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SEMI_CIRCLE');
  }
};

const HEATMAP_DEFAULT_CONFIG = {
  MIN_COLOR: '#FFFFFF',
  LABEL_COLOR: '#000000',
  DEFAULT_SORT: {
    labels: 'Unsorted',
    values: 'Unsorted'
  }
};

const COMMON_DEFAULT_CONFIG = {
  DEFAULT_SORT: {
    labels: 'Unsorted',
    values: 'Unsorted',
    separators: 'ASC'
  }
};

const FUNNEL_DEFAULT_CONFIG = {
  DEFAULT_SORT: {
    labels: 'Unsorted',
    values: 'Unsorted',
    separators: 'ASC'
  }
};

const TREE_MAP_DEFAULT_CONFIG = {
  DEFAULT_SORT: {
    separators: 'ASC'
  }
};

const BUBBLE_DEFAULT_CONFIG = {
  DEFAULT_SORT: {
    labels: 'Unsorted',
    values: 'Unsorted'
  }
};

const SPARKLINE_DEFAULT_CONFIG = {
  DEFAULT_SORT: {
    intervals: 'Unsorted',
    labels: 'Unsorted',
    separators: 'ASC'
  }
};

const COMMON_GAUGE_DEFAULT_CONFIG = {
  DEFAULT_SORT: {
    labels: 'Unsorted',
    values: 'Unsorted',
    separators: 'ASC',
    sup_kpi: 'Unsorted'
  }
};

export default {
  CHART_TYPE,
  OptionsValue: {
    STACK_TYPE_CHART,
    LINE_STYLE,
    LABEL_ROTATION,
    HORIZONTAL_ALIGN,
    VERTICAL_ALIGN,
    LAYOUT_ALIGN,
    ZOOM_TYPE,
    STEP_TYPE,
    RANGE,
    PIE_CHART_STYPE
  },
  MappingValues: {
    AXIS_ALIGN_VALUES,
    STACK_TYPE_CHART_VALUES,
    LINE_STYLE_VALUES,
    LABEL_ROTATION_VALUES,
    HORIZONTAL_ALIGN_VALUES,
    VERTICAL_ALIGN_VALUES,
    LAYOUT_ALIGN_VALUES,
    ZOOM_TYPE_VALUES,
    STEP_TYPE_VALUES
  },

  DefaultConfig: {
    LEGEND_HORIZONTAL_ALIGNMENT: 'izRight',
    LEGEND_VERTICAL_ALIGNMENT: 'izBottom',
    LEGEND_LAYOUT_ALIGNMENT: 'izHorizontal',
    get METRIC_ALL() {
      return localizer.getText('COMMON_LABEL_ALL');
    },
    HeatMap: HEATMAP_DEFAULT_CONFIG,
    Line: COMMON_DEFAULT_CONFIG,
    Column: COMMON_DEFAULT_CONFIG,
    Bar: COMMON_DEFAULT_CONFIG,
    Area: COMMON_DEFAULT_CONFIG,
    Pie: COMMON_DEFAULT_CONFIG,
    Funnel: FUNNEL_DEFAULT_CONFIG,
    Donut: COMMON_DEFAULT_CONFIG,
    Combination: COMMON_DEFAULT_CONFIG,
    TreeMap: TREE_MAP_DEFAULT_CONFIG,
    Bubble: BUBBLE_DEFAULT_CONFIG,
    Scatter: COMMON_DEFAULT_CONFIG,
    Waterfall: COMMON_DEFAULT_CONFIG,
    Sparkline: SPARKLINE_DEFAULT_CONFIG,
    SolidGauge: COMMON_GAUGE_DEFAULT_CONFIG,
    LinearGauge: COMMON_GAUGE_DEFAULT_CONFIG,
    SimpleGauge: COMMON_GAUGE_DEFAULT_CONFIG,
    Map: {}
  },

  getOptions(optionName, isGetKey) {
    const opts = this.OptionsValue[optionName];

    if (isGetKey) {
      const keys = Object.keys(opts);
      const re = {};

      keys.forEach(keyProps => {
        re[keyProps] = keyProps;
      });

      return re;
    }

    return chain(opts)
      .toPairs()
      .map(item => ({
        label: item[1],
        value: item[0]
      }))
      .value();
  }
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ChartDefaultConfig.js